import React, {PureComponent} from 'react'
import withStyles from '@mui/styles/withStyles';
import {Link as RouterLink} from 'react-router-dom';
import Fade from "@mui/material/Fade";
import MenuItem from "@mui/material/MenuItem";
import ListItemIcon from "@mui/material/ListItemIcon";
import DeleteIcon from '@mui/icons-material/Delete';
import ListItemText from "@mui/material/ListItemText";
import Menu from "@mui/material/Menu";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import IconButton from "@mui/material/IconButton";
import CardHeader from "@mui/material/CardHeader";
import Divider from "@mui/material/Divider";
import CardActions from "@mui/material/CardActions";
import Button from "@mui/material/Button";
import Card from "@mui/material/Card";
import {DatePrettifier} from "../../../core/prettifier/DatePrettifier";
import List from "@mui/material/List";
import CardContent from "@mui/material/CardContent";
import ListItem from "@mui/material/ListItem";
import Typography from "@mui/material/Typography";
import {Avatar} from "@mui/material";
import {ColorPicker} from "../../../core/colors/ColorPicker";
import Grid from "@mui/material/Grid";
import Link from "@mui/material/Link";
import {UrlBuilder} from "../../../core/url/UrlBuilder";
import EditIcon from "@mui/icons-material/Edit";
import Collapse from "@mui/material/Collapse";
import FitnessCenterIcon from "@mui/icons-material/FitnessCenter";
import {ExpandLess, ExpandMore} from "@mui/icons-material";
import CardActionArea from "@mui/material/CardActionArea";
import ArchiveIcon from '@mui/icons-material/Archive';
import {withRouter} from "../../../hooks/withRouter";

const useStyles = theme => ({
	floatRight: {
		float: 'right'
	},
	grow: {
		flexGrow: 1,
	},
});

/**
 * A card that can be popped up into fullscreen mode.
 */
class RoutineOverviewCard extends PureComponent {
	static displayName = RoutineOverviewCard.name;

	/**
	 * @param props Containing:
	 *      'routine': The routine.
	 *      'toggleOpenEditRoutinePopup': Trigger the edit routine popuo.
	 *      'deleteRoutine': Deletes itself.
	 *      'archiveRoutine': Archives itself.
	 *      'isInArchivedMode': Is it in archive mode?
	 *      'dragHandle': The handle icon for dragging this card to reorder it.
	 */
	constructor(props) {
		super(props);
		this.state = {
			/**
			 * The routine.
			 */
			routine: this.props.routine,
			/**
			 * The dropdown menu for each routine card.
			 */
			dropDownMenuAnchorEl: null,
			/**
			 * Is the collapse open?
			 */
			isCollapseOpen: false,
			/**
			 * In archive mode?
			 */
			isInArchivedMode: props.isInArchivedMode,
		};
	}
	
	componentWillReceiveProps(nextProps, nextContext) {
		this.setState({
			routine: nextProps.routine,
			isInArchivedMode: nextProps.isInArchivedMode,
		});
	}

	openDropdownMenu = (event) => {
		event.stopPropagation();
		this.setState({
			dropDownMenuAnchorEl: event.currentTarget
		});
	};

	closeDropdownMenu = (event) => {
		event.stopPropagation();
		this.setState({
			dropDownMenuAnchorEl: null
		});
	};
	
	triggerCollapse = (event) => {
		this.setState({
			isCollapseOpen: !this.state.isCollapseOpen
		});
	}

	deleteRoutine = async (event) => {
		event.stopPropagation();
		this.setState({
			dropDownMenuAnchorEl: null
		});
		this.props.deleteRoutine(this.state.routine);
	}

	archiveRoutine = async (event, shouldArchive) => {
		event.stopPropagation();
		this.setState({
			dropDownMenuAnchorEl: null
		});
		this.props.archiveRoutine(shouldArchive);
	}

	toggleOpenEditRoutinePopup = (event) => {
		event.stopPropagation();
		this.setState({
			dropDownMenuAnchorEl: null
		});
		this.props.toggleOpenEditRoutinePopup();	
	}
	
	goToRoutineDetail = () => {
		this.props.navigate(UrlBuilder.routine.routineDetailPage(this.state.routine.id));
	}

	render() {
		const {classes} = this.props;
		const routine = this.state.routine;
		const exerciseCount = routine.exercises ? routine.exercises.length : 0;

		let lastTrained = routine.dateLastLogged
			? DatePrettifier.prettifyLastTrained(new Date(routine.dateLastLogged))
			: null;

		return (
			<Grid item xs={12} sm={12} md={12}>
				<Card key={routine.id} variant="outlined" sx={{ borderRadius: 2.5 }}>
					<CardActionArea onClick={this.goToRoutineDetail}>
						<CardHeader
							sx={{ pb: 0.5 }}
							title={
								<Typography variant="subtitle1" sx={{ fontWeight: 600, lineHeight: 1.3 }}>
									{routine.name}
								</Typography>
							}
							subheader={
								<Typography variant="caption" sx={{ color: 'text.secondary' }}>
									{[lastTrained, routine.description].filter(Boolean).join(' · ')}
								</Typography>
							}
							avatar={
								<Avatar sx={{
									bgcolor: routine.color ?? ColorPicker.pick(routine.name),
									width: 38,
									height: 38,
									fontSize: 16,
									fontWeight: 600,
								}}>
									{routine.name.charAt(0).toUpperCase()}
								</Avatar>
							}
							action={
								<div>
									<IconButton
										id={routine.id}
										name="icon"
										size="small"
										onClick={this.openDropdownMenu}
									>
										<MoreVertIcon fontSize="small"/>
									</IconButton>
									<Menu
										id={routine.id}
										anchorEl={this.state.dropDownMenuAnchorEl}
										open={Boolean(this.state.dropDownMenuAnchorEl && this.state.dropDownMenuAnchorEl.id == routine.id)}
										onClose={this.closeDropdownMenu}
										TransitionComponent={Fade}
									>
										{this.state.isInArchivedMode ?
											<>
												<MenuItem onClick={this.toggleOpenEditRoutinePopup}>
													<ListItemIcon>
														<EditIcon fontSize="small"/>
													</ListItemIcon>
													<ListItemText primary="Edit"/>
												</MenuItem>
												<MenuItem onClick={(e) => this.archiveRoutine(e, false)}>
													<ListItemIcon>
														<ArchiveIcon fontSize="small"/>
													</ListItemIcon>
													<ListItemText primary='Unarchive'/>
												</MenuItem>
												<MenuItem onClick={this.deleteRoutine}>
													<ListItemIcon>
														<DeleteIcon fontSize="small"/>
													</ListItemIcon>
													<ListItemText primary='Delete'/>
												</MenuItem>
											</>
											 : <>
												<MenuItem onClick={this.toggleOpenEditRoutinePopup}>
													<ListItemIcon>
														<EditIcon fontSize="small"/>
													</ListItemIcon>
													<ListItemText primary="Edit"/>
												</MenuItem>
												<MenuItem onClick={(e) => this.archiveRoutine(e, true)}>
													<ListItemIcon>
														<ArchiveIcon fontSize="small"/>
													</ListItemIcon>
													<ListItemText primary='Archive'/>
												</MenuItem>
											 </>
										}
									</Menu>
								</div>
							}
						/>
					</CardActionArea>

					{exerciseCount > 0 && (
						<Collapse in={this.state.isCollapseOpen}>
							<Divider/>
							<CardContent sx={{ py: 1, '&:last-child': { pb: 1 } }}>
								<List dense disablePadding>
									{routine.exercises.map(exercise => (
										<ListItem key={exercise.id} sx={{ px: 0.5, py: 0.25 }}>
											<ListItemIcon sx={{ minWidth: 32 }}>
												<Avatar sx={{
													width: 24,
													height: 24,
													fontSize: 11,
													fontWeight: 600,
													bgcolor: exercise.color ?? ColorPicker.pick(exercise.name),
												}}>
													{exercise.name.charAt(0).toUpperCase()}
												</Avatar>
											</ListItemIcon>
											<ListItemText
												primary={
													<Link
														component={RouterLink}
														to={UrlBuilder.routine.routineDetailPage(routine.id, exercise.routineExerciseId)}
														color="inherit"
														sx={{ fontSize: 13, textDecoration: 'none', '&:hover': { textDecoration: 'underline' } }}
													>
														{exercise.name}
													</Link>
												}
											/>
										</ListItem>
									))}
								</List>
							</CardContent>
						</Collapse>
					)}

					<CardActions disableSpacing sx={{ px: 1.5, py: 0.75 }}>
						{this.props.dragHandle}

						<div className={classes.grow}/>

						{exerciseCount > 0 && (
							<IconButton size="small" onClick={this.triggerCollapse} sx={{ mr: 0.5 }}>
								{this.state.isCollapseOpen ? <ExpandLess fontSize="small"/> : <ExpandMore fontSize="small"/>}
							</IconButton>
						)}

						<Button
							component={RouterLink}
							to={UrlBuilder.routine.routineDetailPage(routine.id)}
							size="small"
							sx={{ textTransform: 'none', fontWeight: 600, fontSize: 13 }}
						>
							Open
						</Button>
					</CardActions>
				</Card>
			</Grid>
		);
	}
}

export default withStyles(useStyles)(withRouter(RoutineOverviewCard));